// F-RPT-N41 — how a screen should name the cost and margin figures for a
// window's cost-of-goods basis. One place, so the Dashboard, the Reports
// overview and the Menu costing tab cannot drift apart.
//
// at_sale (or no field at all, e.g. an older backend): exactly what the
// screens showed before the basis existed.
// purchases: the cost figure is what was bought, so nothing is "projected" and
// cost coverage does not describe it.
// mixed: the labels stay, but the coverage badge would warn about days that
// carry no item costs on purpose, so it is dropped.
import type { MetricKey } from '@/lib/financeGlossary'
import type { CogsBasisWindow } from '@/types'

export interface CogsBasisDisplay {
  purchases: boolean
  /** Label for the cost figure, or null to keep the screen's own label. */
  costLabel: string | null
  costMetric: MetricKey
  /** Sub-label explaining the cost figure, or null to keep the screen's own. */
  costNote: string | null
  marginLabel: string
  marginMetric: MetricKey
  /** Whether to show the "N% covered" text. */
  showCoverage: boolean
  /** Whether to show the completeness badge driven by cost coverage. */
  showCoverageBadge: boolean
}

export function cogsBasisDisplay(basis: CogsBasisWindow | null | undefined): CogsBasisDisplay {
  if (basis === 'purchases') {
    return {
      purchases: true,
      costLabel: 'Cost of goods (purchases)',
      costMetric: 'cogs_purchases',
      costNote: 'From what you bought in this period.',
      marginLabel: 'Margin',
      marginMetric: 'gross_profit',
      showCoverage: false,
      showCoverageBadge: false,
    }
  }
  return {
    purchases: false,
    costLabel: null,
    costMetric: 'cogs',
    costNote: null,
    marginLabel: 'Projected margin',
    marginMetric: 'projected_margin',
    showCoverage: true,
    showCoverageBadge: basis !== 'mixed',
  }
}

/** Menu → Costing, when today's basis is purchases. */
export const ITEM_COSTS_REFERENCE_ONLY =
  'Your profit and loss uses what you buy as cost of goods, so item costs here are for reference only.'
